import React, { useContext, useEffect, useState } from "react";

import {
  Ion,
  Viewer,
  Cartesian3,
  Math,
  createWorldTerrainAsync,
  CesiumTerrainProvider,
  NearFarScalar,
  knockout,
  buildModuleUrl,
  UrlTemplateImageryProvider,
  Credit,
  ProviderViewModel,
  OpenStreetMapImageryProvider,
  SkyAtmosphere,
  ScreenSpaceEventType,
} from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import "cesium/Build/Cesium/Widgets/Animation/Animation.css";
import "cesium/Build/Cesium/Widgets/Animation/lighter.css";
import "cesium/Build/Cesium/Widgets/Timeline/lighter.css";
import "cesium/Build/Cesium/Widgets/lighterShared.css";
import "./3dMain.scss";
import "./bucket.css";

import CesiumNavigation from "cesium-navigation-es6";
import { CesiumContextProvider } from "../../context/CesiumContext";
import { applyRenderQuality } from "../../helper/renderQuality";

function ViewerComponent({ children }) {
  const [viewer, setViewer] = useState();
  useEffect(() => {
    if (!viewer) return;
    let tmpViewer = viewer;
    // var scene = tmpViewer.scene;
    // var globe = scene.globe;

    //hide the moon
    tmpViewer.scene.moon.show = false;

    // Hide objects behind terrain
    tmpViewer.scene.globe.depthTestAgainstTerrain = true;
    tmpViewer.scene.screenSpaceCameraController.minimumZoomDistance = 250;
    tmpViewer.scene.fog.enabled = true;
    tmpViewer.scene.fog.density = 0.0001;
    tmpViewer.scene.fog.minimumBrightness = 1;
    tmpViewer.scene.highDynamicRange = true;

    tmpViewer.scene.globe.showGroundAtmosphere = false;
    tmpViewer.scene.globe.enableLighting = false;
    tmpViewer.scene.skyAtmosphere = new SkyAtmosphere();
    const skyAtmosphere = tmpViewer.scene.skyAtmosphere;
    skyAtmosphere.show = true;
    skyAtmosphere.atmosphereLightIntensity = 70;
    skyAtmosphere.atmosphereRayleighCoefficient.x = 5.5 * 1e-6;
    skyAtmosphere.atmosphereRayleighCoefficient.y = 13 * 1e-6;
    skyAtmosphere.atmosphereRayleighCoefficient.z = 51 * 1e-6;
    skyAtmosphere.atmosphereRayleighScaleHeight = 5900;
    skyAtmosphere.brightnessShift = 0.15;

    const globe = tmpViewer.scene.globe;
    globe.atmosphereLightIntensity = 100;
    globe.atmosphereRayleighCoefficient.x = 8 * 1e-6;
    globe.atmosphereRayleighCoefficient.y = 32 * 1e-6;
    globe.atmosphereRayleighCoefficient.z = 60 * 1e-6;
    const i = 41 * 1e-6;
    globe.atmosphereMieCoefficient = new Cartesian3(i, i, i);
    // globe.atmosphereRayleighScaleHeight = 4500;
    // globe.atmosphereMieScaleHeight = 1e4;
    // globe.atmosphereMieAnisotropy = 1

    tmpViewer.screenSpaceEventHandler.removeInputAction(
      ScreenSpaceEventType.LEFT_DOUBLE_CLICK
    );

    tmpViewer.scene.skyBox.show = false;
    tmpViewer.forceResize();

    tmpViewer.scene.screenSpaceCameraController.inertiaSpin = 0.1;
    tmpViewer.scene.screenSpaceCameraController.inertiaTranslate = 0.1;
    tmpViewer.scene.screenSpaceCameraController.inertiaZoom = 0.1;

    const screenSpaceCameraController =
      viewer.scene.screenSpaceCameraController;

    // Disable camera collision to allow it to go underground
    screenSpaceCameraController.enableCollisionDetection = true;
    // screenSpaceCameraController.tiltEventTypes = 0

    tmpViewer.scene.globe.translucency.frontFaceAlphaByDistance =
      new NearFarScalar(400.0, 0.0, 800.0, 1.0);

    // var viewModel =
    // {
    //   translucencyEnabled: true,
    //   fadeByDistance: true,
    //   showVectorData: false,
    //   //alpha: 0.5,
    //   alpha: 0.8,
    // };

    // knockout.track(viewModel);

    // for (var name in viewModel) {
    //   if (viewModel.hasOwnProperty(name)) {
    //     knockout.getObservable(viewModel, name).subscribe(update(tmpViewer.scene.globe, viewModel));
    //   }
    // }

    // Inject custom CSS into InfoBox iframe (based on Stack Overflow solution)
    const frame = tmpViewer.infoBox.frame;
    frame.addEventListener('load', function () {
      const cssLink = frame.contentDocument.createElement('link');
      cssLink.href = '/flight-info.css';
      cssLink.rel = 'stylesheet';
      cssLink.type = 'text/css';
      frame.contentDocument.head.appendChild(cssLink);
    });

    addNavigation(tmpViewer);
  }, [viewer]);

  useEffect(() => {
    buildModuleUrl.setBaseUrl(window.location.origin);

    createCesiumViewer();
  }, []);

  const createCesiumViewer = async () => {
    Ion.defaultAccessToken = ""; // Disable Cesium ion if not using it

    // Create custom imagery provider view models
    const openStreetMapImageryProvider = new ProviderViewModel({
      name: "OpenStreetMap",
      iconUrl: buildModuleUrl("./logo.png"),
      tooltip: "OpenStreetMap imagery",
      creationFunction: function () {
        return new OpenStreetMapImageryProvider({
          url: "https://a.tile.openstreetmap.org/",
        });
      },
    });

    const burnairProvider = new ProviderViewModel({
      name: "burnair Map",
      iconUrl: buildModuleUrl("./logo.png"),
      tooltip: "burnair Map",
      creationFunction: function () {
        return new UrlTemplateImageryProvider({
          url: "https://tiles.burnair.cloud/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=xKvH1os5tejr0ccKNnhO",
          minimumLevel: 0,
          maximumLevel: 20,
        });
      },
    });

    const viewer = new Viewer("cesiumContainer", {
      imageryProviderViewModels: [burnairProvider],
      baseLayerPicker: true,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      navigationInstructionsInitiallyVisible: false, // Instrucoes de como se usa
      /* seleccao e infobox */
      infoBox: true,
      selectionIndicator: true,
      timeline: false,
      animation: false,
      scene3DOnly: true,
      geocoder: false,
      useDefaultRenderLoop: true,
      showRenderLoopErrors: true,
      automaticallyTrackDataSourceClocks: true,
      orderIndependentTranslucency: true,
      // , skyAtmosphere: false
      contextOptions: {
        webgl: {
          alpha: true,
          // NOTE: this flag only antialiases the *default* framebuffer, which this
          // app never draws into — with a depth texture available Cesium renders
          // through `view.globeDepth.framebuffer`, and `highDynamicRange` below
          // additionally forces the post-process chain on. It is kept only as a
          // fallback for contexts with neither. Real antialiasing is decided in
          // helper/renderQuality.js (`scene.msaaSamples`).
          antialias: true,
        },
      },
      moon: false, // line to disable the Moon
    });

    // Render resolution + antialiasing. Must happen before the first frame.
    applyRenderQuality(viewer);

    setTerrain(viewer);
    window.viewer = viewer;
    setViewer(viewer);
  };

  function addNavigation(tmpViewer) {
    const options = {};

    //default is 3
    options.duration = 4;

    options.enableCompass = true;
    options.enableZoomControls = true;
    options.enableDistanceLegend = true;
    options.enableCompassOuterRing = true;

    options.resetTooltip = "Reset View";
    options.zoomInTooltip = "Zoom In";
    options.zoomOutTooltip = "Zoom Out";

    new CesiumNavigation(tmpViewer, options);
  }

  const setTerrain = async (viewer) => {
    let url = "./tiles/terrain-quantized-mesh-v2/";
    viewer.terrainProvider = await CesiumTerrainProvider.fromUrl(url);
  };

  function update(globe, viewModel) {
    console.log("update");
    globe.translucency.enabled = viewModel.translucencyEnabled;

    var alpha = Number(viewModel.alpha);
    alpha = !isNaN(alpha) ? alpha : 1.0;
    alpha = Math.clamp(alpha, 0.0, 1.0);

    globe.translucency.frontFaceAlphaByDistance.nearValue = alpha;
    globe.translucency.frontFaceAlphaByDistance.farValue =
      viewModel.fadeByDistance ? 1.0 : alpha;
  }

  return (
    <>
      <div className="no-select-item" id="igcFileDisplay">
        <div className="no-select-item" id="cesiumContainer"></div>
      </div>
      <div
        className="logo-container"
        style={{ backgroundImage: `url('./logo.png')` }}
      ></div>
      <CesiumContextProvider viewer={viewer}>
        {viewer ? children : <></>}
      </CesiumContextProvider>
    </>
  );
}

export default ViewerComponent;
